import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { limitOrderAPI, assetAPI } from '../../utils/api';
import { formatCurrency, formatDate, getStatusBadgeClass, formatStatusText } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Target, X } from 'lucide-react';

const Targets = () => {
  const [assets, setAssets] = useState([]);
  const [limitOrders, setLimitOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    trade_type: 'buy',
    asset_symbol: '',
    amount: '',
    target_price: '',
    expires_in_days: 30
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assetsRes, ordersRes] = await Promise.all([
        assetAPI.getAll(),
        limitOrderAPI.getAll()
      ]);
      setAssets(assetsRes.data.filter(a => a.type === 'crypto'));
      setLimitOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await limitOrderAPI.create({
        trade_type: formData.trade_type,
        asset_symbol: formData.asset_symbol,
        amount: parseFloat(formData.amount),
        target_price: parseFloat(formData.target_price),
        expires_in_days: parseInt(formData.expires_in_days)
      });
      toast.success('Target order created successfully!');
      setFormData({
        trade_type: 'buy',
        asset_symbol: '',
        amount: '',
        target_price: '',
        expires_in_days: 30
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create target order');
    }
  };

  const handleCancel = async (orderId) => {
    try {
      await limitOrderAPI.cancel(orderId);
      toast.success('Target order cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="loading-spinner"></div></div>;

  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h1 className="text-4xl font-bold text-black mb-2">Target Orders</h1>
        <p className="text-gray-600">Set price targets and execute automatically</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Target Order Form */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-black mb-4 flex items-center">
            <Target className="w-5 h-5 mr-2 text-[#FF5A5A]" />
            Create Target Order
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-black">Type</Label>
              <Select value={formData.trade_type} onValueChange={(v) => setFormData({ ...formData, trade_type: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">Buy</SelectItem>
                  <SelectItem value="sell">Sell</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-black">Asset</Label>
              <Select value={formData.asset_symbol} onValueChange={(v) => setFormData({ ...formData, asset_symbol: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select asset" />
                </SelectTrigger>
                <SelectContent>
                  {assets.map(a => <SelectItem key={a.symbol} value={a.symbol}>{a.name} ({a.symbol})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-black">Amount</Label>
              <Input
                type="number"
                step="0.00000001"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="mt-1"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <Label className="text-black">Target Price (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.target_price}
                onChange={(e) => setFormData({ ...formData, target_price: e.target.value })}
                className="mt-1"
                placeholder="0.00"
                required
              />
            </div>

            <div>
              <Label className="text-black">Expires In (Days)</Label>
              <Input
                type="number"
                value={formData.expires_in_days}
                onChange={(e) => setFormData({ ...formData, expires_in_days: e.target.value })}
                className="mt-1"
                min="1"
                max="365"
                required
              />
            </div>

            <Button type="submit" className="w-full text-white hover:opacity-90" style={{ background: '#062136' }}>
              Create Target Order
            </Button>
          </form>
        </Card>

        {/* Active Target Orders */}
        <Card className="p-6">
          <h2 className="text-xl font-bold text-black mb-4">Active Targets</h2>
          <div className="space-y-3">
            {limitOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No target orders yet</p>
            ) : (
              limitOrders.map(order => (
                <div key={order.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-black">
                        {order.trade_type.toUpperCase()} {order.amount} {order.asset_symbol}
                      </p>
                      <p className="text-sm text-gray-600">
                        Target: {formatCurrency(order.target_price)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCancel(order.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Expires: {formatDate(order.expires_at)}</span>
                    <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                      {formatStatusText(order.status)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Targets;
